import { useState } from 'react';
import { C, DEGRADE_ACTION } from '../../theme';
import { Titre, SousTitreSection, Pastille, Bascule, Chargement, Panne } from '../composants/base';
import { api } from '../../api/routes';
import { useDonnees, messageDErreur } from '../../api/useDonnees';
import { CONNEXIONS, CATEGORIES } from '../donnees';
import { AjouterQuelquun } from '../composants/AjouterQuelquun';
import { FichePersonne } from '../composants/FichePersonne';
import { FaitsDe } from '../composants/FaitsDe';
import { IdentifiantsEnfant } from '../composants/IdentifiantsEnfant';
import { NotificationsAppareil } from '../composants/NotificationsAppareil';
import { BoutonDeuxGestes } from '../composants/BoutonDeuxGestes';
import { LigneRenommable } from '../composants/LigneRenommable';
import { MesDonnees } from '../composants/MesDonnees';
import { MotDePasse } from '../composants/MotDePasse';
import { PlanDuFoyer } from '../composants/PlanDuFoyer';
import { agePourAffichage } from '../format';
import { CarteApparence } from '../composants/CarteApparence';
import type { Theme } from '../theme-contexte';
import type { SujetDeFait, Utilisateur } from '../../api/types';

export function Reglages({
  utilisateur,
  version,
  onDeconnexion,
  signaler,
  rafraichir,
  theme,
}: {
  utilisateur: Utilisateur;
  version: number;
  onDeconnexion: () => void;
  signaler: (message: string) => void;
  rafraichir: () => void;
  theme: { libelle: string; choix: Theme; changer: (t: Theme) => void; cycler: () => void };
}) {
  const etat = useDonnees((signal) => api.foyer(signal), version);
  // Ce que Colette sait du foyer (etape B1). `versionFaits` la recharge apres chaque
  // ajout, correction ou oubli, sans recharger le foyer entier.
  const [versionFaits, setVersionFaits] = useState(0);
  const etatFaits = useDonnees((signal) => api.faits(signal), `${version}-${versionFaits}`);
  const faits = etatFaits.donnees?.faits ?? [];
  const rechargerLesFaits = () => setVersionFaits((n) => n + 1);
  const faitsDe = (sujet: SujetDeFait) =>
    faits.filter((fait) =>
      sujet.type === 'foyer'
        ? fait.sujet.type === 'foyer'
        : fait.sujet.type === sujet.type && fait.sujet.id === sujet.id,
    );
  const [deconnexionEnCours, setDeconnexionEnCours] = useState(false);

  const seDeconnecter = async () => {
    if (deconnexionEnCours) return;
    setDeconnexionEnCours(true);
    try {
      await api.deconnexion();
    } catch {
      // Le jeton est peut-etre deja invalide cote serveur : on ferme la session locale
      // quoi qu'il arrive. Rester connecte apres avoir demande a sortir serait pire.
    }
    onDeconnexion();
  };

  const foyer = etat.donnees?.household ?? null;
  const estAdulte =
    foyer?.members.find((m) => m.userId === utilisateur.id)?.role === 'ADULT';

  return (
    <div
      style={{
        flex: '1 1 0',
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        padding: '28px 20px 16px 20px',
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <Titre>Colette</Titre>
        <div style={{ fontSize: 13, color: C.secondaire }}>Ton compte, ton foyer et tes réglages.</div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <SousTitreSection>Compte</SousTitreSection>
        <LigneRenommable
          titre={utilisateur.displayName}
          detail={utilisateur.email}
          valeur={utilisateur.displayName}
          libelleChamp="Mon nom"
          ariaBouton="Changer mon nom"
          modifiable
          onEnregistrer={async (nouveau) => {
            try {
              await api.changerMonNom(nouveau);
              signaler(`Colette t’appellera ${nouveau}`);
              rafraichir();
            } catch (cause) {
              signaler(messageDErreur(cause));
              throw cause;
            }
          }}
        />
        <MotDePasse signaler={signaler} />
      </div>

      {etat.chargement && foyer === null ? (
        <Chargement texte="Chargement du foyer…" />
      ) : etat.erreur !== null && foyer === null ? (
        <Panne message={etat.erreur} onReessayer={etat.recharger} />
      ) : foyer !== null ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <SousTitreSection>Foyer</SousTitreSection>
          <LigneRenommable
            titre={foyer.name}
            detail="Le foyer de Colette"
            valeur={foyer.name}
            libelleChamp="Nom du foyer"
            ariaBouton="Changer le nom du foyer"
            modifiable={estAdulte}
            onEnregistrer={async (nouveau) => {
              try {
                await api.renommerFoyer(nouveau);
                signaler(`Foyer renommé : ${nouveau}`);
                rafraichir();
              } catch (cause) {
                signaler(messageDErreur(cause));
                throw cause;
              }
            }}
          />
          <FuseauDuFoyer
            fuseau={foyer.timezone}
            modifiable={estAdulte}
            onChange={(message) => {
              signaler(message);
              rafraichir();
            }}
            signaler={signaler}
          />
          <VilleDuFoyer
            ville={foyer.city ?? null}
            modifiable={estAdulte}
            onDefinie={(message) => {
              signaler(message);
              rafraichir();
            }}
            signaler={signaler}
          />
          {foyer.members.map((membre) => (
            <div key={membre.id} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <FichePersonne
              type="membre"
              id={membre.id}
              titre={membre.displayName}
              detail={
                (membre.role === 'ADULT' ? 'Adulte' : 'Enfant') +
                (membre.birthDate != null ? ` · ${agePourAffichage(membre.birthDate)}` : '')
              }
              birthDate={membre.birthDate}
              modifiable={estAdulte}
              retirable={membre.userId === null}
              onFait={(message) => {
                signaler(message);
                rafraichir();
              }}
              signaler={signaler}
            />
            {/* Reparer l'acces d'un compte ENFANT : l'adresse mal tapee a la creation
                rendait le compte inutilisable et irreparable. Rien pour un adulte (le
                serveur refuserait) ni pour un membre sans compte. */}
            {estAdulte && membre.role === 'CHILD' && membre.userId !== null && membre.email ? (
              <IdentifiantsEnfant
                membreId={membre.id}
                prenom={membre.displayName}
                email={membre.email}
                onFait={(message) => {
                  signaler(message);
                  rafraichir();
                }}
                signaler={signaler}
              />
            ) : null}
            <FaitsDe
              sujet={{ type: 'membre', id: membre.id }}
              aPropos={membre.displayName}
              faits={faitsDe({ type: 'membre', id: membre.id })}
              onChange={rechargerLesFaits}
              signaler={signaler}
            />
            </div>
          ))}
          {foyer.persons.map((personne) => (
            <div key={personne.id} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <FichePersonne
              type="personne"
              id={personne.id}
              titre={personne.displayName}
              detail={
                'Connue de Colette' +
                (personne.birthDate != null ? ` · ${agePourAffichage(personne.birthDate)}` : '')
              }
              birthDate={personne.birthDate}
              modifiable={estAdulte}
              retirable
              onFait={(message) => {
                signaler(message);
                rafraichir();
              }}
              signaler={signaler}
            />
            <FaitsDe
              sujet={{ type: 'personne', id: personne.id }}
              aPropos={personne.displayName}
              faits={faitsDe({ type: 'personne', id: personne.id })}
              onChange={rechargerLesFaits}
              signaler={signaler}
            />
            </div>
          ))}

          {/* Ce que Colette sait du FOYER, et pas de quelqu'un en particulier : la gare,
              le jour des poubelles, l'ecole. */}
          <FaitsDe
            sujet={{ type: 'foyer' }}
            aPropos="le foyer"
            titre="Ce que Colette sait du foyer"
            faits={faitsDe({ type: 'foyer' })}
            onChange={rechargerLesFaits}
            signaler={signaler}
          />

          {/* Seul un adulte peut ajouter quelqu'un : plutot que de laisser l'API rendre
              un 403, on ne propose pas le bouton. */}
          {estAdulte ? (
            <AjouterQuelquun
              onAjoute={(message) => {
                signaler(message);
                rafraichir();
              }}
            />
          ) : null}
        </div>
      ) : null}

      <PlanDuFoyer version={version} />

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <SousTitreSection>Notifications</SousTitreSection>
        <NotificationsAppareil signaler={signaler} />
      </div>

      {/* Ces deux sections viennent du prototype. Elles sont presentees en LECTURE SEULE :
          rien cote serveur ne les porte encore, et un interrupteur qui ne commande rien
          ferait croire a un reglage actif. */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <SousTitreSection>Connexions</SousTitreSection>
        <div style={{ fontSize: 12, color: C.tertiaire, lineHeight: 1.5, marginTop: -2 }}>
          Colette ne lit que ce que tu autorises, et tu peux couper à tout moment.
        </div>
        {CONNEXIONS.map((connexion) =>
          connexion.cle === 'cal' ? (
            <ConnexionCalendrier
              key={connexion.cle}
              version={version}
              signaler={signaler}
              rafraichir={rafraichir}
            />
          ) : connexion.cle === 'meteo' ? (
            <ConnexionMeteo key={connexion.cle} ville={foyer?.city ?? null} />
          ) : (
            <div
              key={connexion.cle}
              style={{
                background: C.carte,
                border: `1px solid ${C.bordure}`,
                borderRadius: 14,
                padding: '13px 16px',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
              }}
            >
              <div style={{ flex: '1 1 0', display: 'flex', flexDirection: 'column', gap: 2 }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: C.encre }}>{connexion.label}</div>
                <div style={{ fontSize: 12, color: C.secondaire, lineHeight: 1.4 }}>
                  {connexion.description}
                </div>
              </div>
              <Pastille fond={C.page} couleur={C.tertiaire}>
                Bientôt
              </Pastille>
            </div>
          ),
        )}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <SousTitreSection>Ce que Colette saura gérer</SousTitreSection>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 9 }}>
          {CATEGORIES.map((label) => (
            <div
              key={label}
              style={{
                borderRadius: 999,
                padding: '7px 13px',
                fontSize: 13,
                fontWeight: 700,
                background: C.carte,
                color: C.tertiaire,
                border: `1px solid ${C.bordure}`,
              }}
            >
              {label}
            </div>
          ))}
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <SousTitreSection>Apparence</SousTitreSection>
        <CarteApparence libelle={theme.libelle} choix={theme.choix} onChanger={theme.changer} />
      </div>

      {/* Emporter ses donnees, ou partir : les deux droits que la politique de
          confidentialite promet en ligne depuis le premier jour. */}
      <MesDonnees
        signaler={signaler}
        onCompteSupprime={() => {
          signaler('Ton compte a été supprimé');
          onDeconnexion();
        }}
      />

      <button
        type="button"
        onClick={() => void seDeconnecter()}
        style={{
          marginTop: 4,
          height: 48,
          borderRadius: 14,
          border: `2px solid ${C.bordure}`,
          background: C.carte,
          color: C.corailTexte,
          fontSize: 15,
          fontWeight: 800,
          fontFamily: 'inherit',
          cursor: 'pointer',
        }}
      >
        {deconnexionEnCours ? 'Déconnexion…' : 'Se déconnecter'}
      </button>

      <div style={{ textAlign: 'center', fontSize: 12, color: C.tertiaire, padding: '4px 0' }}>
        Colette · version d'essai privée
      </div>
    </div>
  );
}

/**
 * La connexion Google Agenda du COMPTE (chaque adulte connecte le sien).
 *
 * Quatre etats, tous montres honnetement : « A configurer » tant que le serveur n'est
 * pas configure (le code est pret, il manque les parametres Google), « Connecter »
 * sinon, l'email connecte, ou « A reconnecter » quand Google a revoque le jeton —
 * chaque semaine en mode Test, c'est attendu.
 */
function ConnexionCalendrier({
  version,
  signaler,
  rafraichir,
}: {
  version: number;
  signaler: (message: string) => void;
  rafraichir: () => void;
}) {
  const etat = useDonnees((signal) => api.etatGoogle(signal), version);
  const [occupe, setOccupe] = useState(false);

  const connecter = async () => {
    if (occupe) return;
    setOccupe(true);
    try {
      const { url } = await api.demarrerGoogle();
      // Navigation complete : Google prend la main, puis renvoie vers /app.
      window.location.assign(url);
    } catch (cause) {
      signaler(messageDErreur(cause));
      setOccupe(false);
    }
  };

  const deconnecter = async () => {
    if (occupe) return;
    setOccupe(true);
    try {
      await api.deconnecterGoogle();
      signaler('Google Agenda déconnecté');
      rafraichir();
    } catch (cause) {
      signaler(messageDErreur(cause));
    } finally {
      setOccupe(false);
    }
  };

  const donnees = etat.donnees;

  /**
   * L'interrupteur d'ecriture : un VRAI interrupteur. Son etat vient du serveur rechargé
   * — un interrupteur qui mentirait sur ce qu'il commande serait pire que pas d'interrupteur.
   */
  const basculerEcriture = async (actif: boolean) => {
    if (occupe) return;
    setOccupe(true);
    try {
      await api.definirEcritureGoogle(actif);
      signaler(
        actif
          ? 'Tes événements validés partiront dans Google Agenda'
          : 'Tes événements resteront dans Colette',
      );
      rafraichir();
    } catch (cause) {
      signaler(messageDErreur(cause));
    } finally {
      setOccupe(false);
    }
  };

  return (
    <div
      style={{
        background: C.carte,
        border: `1px solid ${C.bordure}`,
        borderRadius: 14,
        padding: '13px 16px',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <div style={{ flex: '1 1 0', display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: C.encre }}>Google Agenda</div>
        <div style={{ fontSize: 12, color: C.secondaire, lineHeight: 1.4 }}>
          {donnees === null
            ? 'Vérification…'
            : donnees.etat === 'connecte'
              ? `Connecté : ${donnees.email}. Tes événements Google apparaissent dans le planning.`
              : donnees.etat === 'reconnexion'
                ? `L'autorisation de ${donnees.email} a expiré — c'est normal chaque semaine pendant la phase de test.`
                : donnees.etat === 'non-configure'
                  ? 'Le code est prêt — la configuration du serveur reste à faire (guide du déploiement).'
                  : 'Tes événements Google dans ton planning, en lecture.'}
        </div>
      </div>

      {donnees === null ? null : donnees.etat === 'non-configure' ? (
        <Pastille fond={C.page} couleur={C.tertiaire}>
          À configurer
        </Pastille>
      ) : donnees.etat === 'connecte' ? (
        <BoutonDeuxGestes
          libelle="Déconnecter"
          libelleArme="Confirmer ?"
          aria="Déconnecter"
          ariaArme="Confirmer la déconnexion de Google Agenda"
          onConfirmer={() => void deconnecter()}
          occupe={occupe}
          style={{
            border: `1px solid ${C.bordure}`,
            background: C.carte,
            color: C.secondaire,
            borderRadius: 10,
            padding: '7px 12px',
            fontSize: 13,
            fontWeight: 800,
            fontFamily: 'inherit',
            cursor: occupe ? 'default' : 'pointer',
            flexShrink: 0,
          }}
          styleArme={{ color: C.corailTexte, borderColor: C.corailTexte }}
        />
      ) : (
        <button
          type="button"
          disabled={occupe}
          onClick={() => void connecter()}
          style={{
            border: 'none',
            background: occupe ? C.inactif : DEGRADE_ACTION,
            color: '#FFFFFF',
            borderRadius: 10,
            padding: '8px 14px',
            fontSize: 13,
            fontWeight: 800,
            fontFamily: 'inherit',
            cursor: occupe ? 'default' : 'pointer',
            flexShrink: 0,
          }}
        >
          {donnees.etat === 'reconnexion' ? 'Reconnecter' : 'Connecter'}
        </button>
      )}
      </div>

      {/* L'ecriture VERS Google : le second sens, coupable a tout moment. */}
      {donnees !== null && donnees.etat === 'connecte' ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            borderTop: `1px solid ${C.bordure}`,
            paddingTop: 10,
          }}
        >
          <div style={{ flex: 1, fontSize: 12.5, color: C.secondaire, lineHeight: 1.4 }}>
            Ajouter mes événements validés à Google Agenda
          </div>
          <Bascule
            actif={donnees.ecritureActive}
            libelle="Ajouter mes événements à Google Agenda"
            onChange={() => void basculerEcriture(!donnees.ecritureActive)}
          />
        </div>
      ) : null}
    </div>
  );
}

/**
 * Le fuseau du foyer — un CHOIX, pas une donnee technique.
 *
 * Le porteur a delibérément mis la France alors qu'il vit a Dakar, et il observe des
 * decalages avec Google Agenda sans en connaitre la cause. L'ecran ne decide donc rien a
 * sa place : il rend le reglage visible, et NOMME sa consequence.
 */
const FUSEAUX_COURANTS: { valeur: string; libelle: string }[] = [
  { valeur: 'Africa/Dakar', libelle: 'Sénégal (Dakar)' },
  { valeur: 'Europe/Paris', libelle: 'France (Paris)' },
  { valeur: 'Europe/Brussels', libelle: 'Belgique (Bruxelles)' },
  { valeur: 'Europe/London', libelle: 'Royaume-Uni (Londres)' },
  { valeur: 'Africa/Abidjan', libelle: "Côte d'Ivoire (Abidjan)" },
  { valeur: 'Africa/Casablanca', libelle: 'Maroc (Casablanca)' },
  { valeur: 'America/Montreal', libelle: 'Québec (Montréal)' },
];

function FuseauDuFoyer({
  fuseau,
  modifiable,
  onChange,
  signaler,
}: {
  fuseau: string;
  modifiable: boolean;
  onChange: (message: string) => void;
  signaler: (message: string) => void;
}) {
  const [edition, setEdition] = useState(false);
  const [choix, setChoix] = useState(fuseau);
  const [envoi, setEnvoi] = useState(false);

  const enregistrer = async () => {
    if (envoi || choix === fuseau) {
      setEdition(false);
      return;
    }
    setEnvoi(true);
    try {
      const reponse = await api.definirFuseau(choix);
      setEdition(false);
      onChange(`Colette calcule maintenant en ${reponse.household.timezone}`);
    } catch (cause) {
      signaler(messageDErreur(cause));
    } finally {
      setEnvoi(false);
    }
  };

  const nomLisible = FUSEAUX_COURANTS.find((f) => f.valeur === fuseau)?.libelle ?? fuseau;

  if (!edition) {
    return (
      <div
        style={{
          background: C.carte,
          border: `1px solid ${C.bordure}`,
          borderRadius: 14,
          padding: '13px 16px',
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: C.encre }}>
              Heure du foyer : {nomLisible}
            </div>
            <div style={{ fontSize: 12, color: C.secondaire }}>{fuseau}</div>
          </div>
          {modifiable ? (
            <button
              type="button"
              className="pressable"
              aria-label="Changer le fuseau horaire"
              onClick={() => setEdition(true)}
              style={{
                border: `1px solid ${C.violetBordure}`,
                background: C.carte,
                color: C.violetTexte,
                borderRadius: 10,
                padding: '7px 12px',
                fontSize: 13,
                fontWeight: 800,
                fontFamily: 'inherit',
                cursor: 'pointer',
                flexShrink: 0,
              }}
            >
              Changer
            </button>
          ) : null}
        </div>
        <div
          style={{
            fontSize: 12,
            color: C.tertiaire,
            lineHeight: 1.5,
            borderTop: `1px solid ${C.bordure}`,
            paddingTop: 8,
          }}
        >
          Colette calcule tout dans cette heure-là : les horaires affichés, le changement de
          journée à minuit et la revue du matin. Si ton téléphone est dans un autre pays, le
          même rendez-vous s’affichera à une heure différente dans Google Agenda — c’est
          normal, les deux ne comptent pas dans le même fuseau.
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void enregistrer();
      }}
      style={{
        background: C.carte,
        border: `1px solid ${C.violetBordure}`,
        borderRadius: 14,
        padding: '13px 16px',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}
    >
      <select
        value={choix}
        onChange={(e) => setChoix(e.target.value)}
        aria-label="Fuseau horaire du foyer"
        style={{
          height: 42,
          border: `1px solid ${C.bordure}`,
          borderRadius: 11,
          padding: '0 10px',
          fontSize: 16,
          fontFamily: 'inherit',
          color: C.encre,
          background: C.carte,
        }}
      >
        {/* Le fuseau actuel figure toujours, meme s'il n'est pas dans la liste courte. */}
        {FUSEAUX_COURANTS.some((f) => f.valeur === fuseau) ? null : (
          <option value={fuseau}>{fuseau}</option>
        )}
        {FUSEAUX_COURANTS.map((f) => (
          <option key={f.valeur} value={f.valeur}>
            {f.libelle}
          </option>
        ))}
      </select>
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          type="submit"
          disabled={envoi}
          className="pressable"
          style={{
            flex: 1,
            height: 42,
            border: 'none',
            borderRadius: 11,
            background: envoi ? C.inactif : DEGRADE_ACTION,
            color: '#FFFFFF',
            fontSize: 14,
            fontWeight: 800,
            fontFamily: 'inherit',
            cursor: envoi ? 'default' : 'pointer',
          }}
        >
          {envoi ? '…' : 'Enregistrer'}
        </button>
        <button
          type="button"
          className="pressable"
          onClick={() => {
            setChoix(fuseau);
            setEdition(false);
          }}
          style={{
            height: 42,
            padding: '0 14px',
            border: `1px solid ${C.bordure}`,
            borderRadius: 11,
            background: C.carte,
            color: C.secondaire,
            fontSize: 14,
            fontWeight: 700,
            fontFamily: 'inherit',
            cursor: 'pointer',
          }}
        >
          Annuler
        </button>
      </div>
    </form>
  );
}

/**
 * La meteo n'est PAS « Bientôt » : elle marche des que le foyer a une ville (releve
 * du porteur, 2026-08-27). La carte dit donc l'etat REEL : active avec la ville, ou
 * le geste qui manque — jamais une promesse floue.
 */
function ConnexionMeteo({ ville }: { ville: string | null }) {
  return (
    <div
      style={{
        background: C.carte,
        border: `1px solid ${C.bordure}`,
        borderRadius: 14,
        padding: '13px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
      }}
    >
      <div style={{ flex: '1 1 0', display: 'flex', flexDirection: 'column', gap: 2 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: C.encre }}>Météo</div>
        <div style={{ fontSize: 12, color: C.secondaire, lineHeight: 1.4 }}>
          {ville === null
            ? 'Renseigne la ville du foyer ci-dessus : la météo s’active toute seule.'
            : `Prévisions de ${ville} dans Aujourd’hui et les propositions de Colette.`}
        </div>
      </div>
      {ville === null ? (
        <Pastille fond={C.page} couleur={C.tertiaire}>
          À configurer
        </Pastille>
      ) : (
        <Pastille fond={C.mentheFond} couleur={C.mentheTexte}>
          Active
        </Pastille>
      )}
    </div>
  );
}

/**
 * La ville du foyer : premiere brique de la memoire, et ce qui alimente la meteo.
 * Le serveur geocode la saisie et rend le nom qu'il a RETENU — l'utilisateur voit donc
 * ce que Colette a compris, pas ce qu'il a tape.
 */
function VilleDuFoyer({
  ville,
  modifiable,
  onDefinie,
  signaler,
}: {
  ville: string | null;
  modifiable: boolean;
  onDefinie: (message: string) => void;
  signaler: (message: string) => void;
}) {
  const [edition, setEdition] = useState(false);
  const [saisie, setSaisie] = useState('');
  const [envoi, setEnvoi] = useState(false);

  const definir = async () => {
    const city = saisie.trim();
    if (city === '' || envoi) return;
    setEnvoi(true);
    try {
      const reponse = await api.definirVille(city);
      setEdition(false);
      setSaisie('');
      onDefinie(`Ville du foyer : ${reponse.household.city}`);
    } catch (cause) {
      signaler(messageDErreur(cause));
    } finally {
      setEnvoi(false);
    }
  };

  if (!edition) {
    return (
      <div
        style={{
          background: C.carte,
          border: `1px solid ${C.bordure}`,
          borderRadius: 14,
          padding: '13px 16px',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
        }}
      >
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: C.encre }}>
            {ville ?? 'Ville non renseignée'}
          </div>
          <div style={{ fontSize: 12, color: C.secondaire }}>
            {ville === null
              ? 'Ajoute-la pour que Colette connaisse ta météo.'
              : 'Sert à la météo et aux propositions de Colette.'}
          </div>
        </div>
        {modifiable ? (
          <button
            type="button"
            onClick={() => setEdition(true)}
            style={{
              border: `1px solid ${C.violetBordure}`,
              background: C.carte,
              color: C.violetTexte,
              borderRadius: 10,
              padding: '7px 12px',
              fontSize: 13,
              fontWeight: 800,
              fontFamily: 'inherit',
              cursor: 'pointer',
              flexShrink: 0,
            }}
          >
            {ville === null ? 'Renseigner' : 'Changer'}
          </button>
        ) : null}
      </div>
    );
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void definir();
      }}
      style={{
        background: C.carte,
        border: `1px solid ${C.violetBordure}`,
        borderRadius: 14,
        padding: '13px 16px',
        display: 'flex',
        gap: 8,
      }}
    >
      <input
        value={saisie}
        onChange={(e) => setSaisie(e.target.value)}
        placeholder="Dakar, Paris, Beauvais…"
        aria-label="Ville du foyer"
        autoFocus
        style={{
          flex: 1,
          minWidth: 0,
          height: 42,
          border: `1px solid ${C.bordure}`,
          borderRadius: 11,
          padding: '0 12px',
          fontSize: 16,
          fontFamily: 'inherit',
          color: C.encre,
          // Sans fond declare, un input prend le BLANC du navigateur — et l'encre de
          // Colette etant claire, on n'y voit plus ce qu'on tape.
          background: C.carte,
          outline: 'none',
        }}
      />
      <button
        type="submit"
        disabled={envoi || saisie.trim() === ''}
        style={{
          height: 42,
          padding: '0 14px',
          border: 'none',
          borderRadius: 11,
          background: envoi || saisie.trim() === '' ? C.inactif : DEGRADE_ACTION,
          color: '#FFFFFF',
          fontSize: 14,
          fontWeight: 800,
          fontFamily: 'inherit',
          cursor: envoi || saisie.trim() === '' ? 'default' : 'pointer',
        }}
      >
        {envoi ? '…' : 'OK'}
      </button>
    </form>
  );
}

function Ligne({ titre, detail }: { titre: string; detail: string }) {
  return (
    <div
      style={{
        background: C.carte,
        border: `1px solid ${C.bordure}`,
        borderRadius: 14,
        padding: '13px 16px',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      <div style={{ fontSize: 15, fontWeight: 700, color: C.encre }}>{titre}</div>
      <div style={{ fontSize: 12, color: C.secondaire }}>{detail}</div>
    </div>
  );
}
